<script setup lang="ts">
import { computed, onUnmounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  Server,
  Plus,
  Pencil,
  Trash2,
  HardDrive,
  Network,
  RefreshCw,
  AlertTriangle,
  Gauge,
} from 'lucide-vue-next'
import Card from '@/components/ui/Card.vue'
import Progress from '@/components/ui/Progress.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import Modal from '@/components/ui/Modal.vue'
import Badge from '@/components/ui/Badge.vue'
import StatusDot from '@/components/ui/StatusDot.vue'
import Empty from '@/components/ui/Empty.vue'
import Spinner from '@/components/ui/Spinner.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import {
  addDevice,
  getClusterInvite,
  listAllowedDevices,
  getClusterStatus,
  getDeviceMeta,
  getPairingOutcome,
  removeDevice,
  setNodeSettings,
  updateDeviceMeta,
  type AddDeviceResponse,
  type ClusterInvite,
  type AllowedDevice,
  type ClusterMember,
  type ClusterStatus,
  type DeviceMeta,
  type NodeMode,
} from '@/api/cluster'
import { useToast } from '@/composables/useToast'
import { copyToClipboard } from '@/composables/useClipboard'
import api from '@/api/client'

const { t } = useI18n()
const toast = useToast()
const qc = useQueryClient()

const statusQuery = useQuery({
  queryKey: ['cluster', 'status'],
  queryFn: () => getClusterStatus(),
  refetchInterval: 5000,
  // v3.19.28: 全局 refetchOnWindowFocus=false + 轮询默认不在后台跑 →
  // 双面板对照使用时, 没焦点的那个节点列表彻底冻结 ("必须手动点刷新")。
  // 集群页轮询放开后台标志, 5s 节奏不变, 后台标签页也保持新鮮。
  refetchIntervalInBackground: true,
})

const metaQuery = useQuery({
  queryKey: ['cluster', 'device-meta'],
  queryFn: () => getDeviceMeta(),
})

// v3.13.55: 待确认配对轮询与接受/拒绝已上移至全局 PairingBanner (MainLayout)。

const nodes = computed<ClusterMember[]>(() => {
  const s = statusQuery.data.value as (ClusterStatus & { nodes?: ClusterMember[] }) | undefined
  if (!s) return []
  return (s.nodes ?? s.members ?? []) as ClusterMember[]
})

// R13: gossip 无主架构 — 不存在 leader 概念, 移除恒真的主节点徽章
const selfNode = computed<ClusterMember | null>(() => {
  const s = statusQuery.data.value
  if (!s || !s.node_id) return null
  return (
    nodes.value.find((n) => n.id === s.node_id) ?? {
      id: s.node_id,
      name: metaQuery.data.value?.name ?? s.node_id,
      mode: s.mode ?? metaQuery.data.value?.mode,
      status: 'self',
      online: true,
    }
  )
})

const editingNode = ref<ClusterMember | null>(null)
const showEdit = ref(false)
const showAdd = ref(false)
const showRemoveConfirm = ref<ClusterMember | null>(null)

const editForm = reactive({
  name: '',
  group: '',
  mode: 'both' as NodeMode,
  data_dir: '',
})

const addForm = reactive({
  device_id: '',
  // v3.19.2 恢复手动地址输入。v3.13.55 删掉它的理由是"discovery 已能
  // 自动发现同网段节点" —— 对 vm00/vm01 成立, 对跨公网的两台不成立:
  // 对方隔着 NAT/EIP, 本机既广播不到也拿不到对方地址, 于是只能对着
  // 一个设备 ID 空转。折叠在"高级设置"里, 不填仍走纯 ID 流程。
  addr: '',
})

const showAdvanced = ref(false)

/** 解析用户填的地址 → { grpcAddrs, discoveryAddrs }。空输入返回全空。 */
function parseAddrInput(raw: string): { grpcAddrs: string[]; discoveryAddrs: string[] } {
  const grpcAddrs: string[] = []
  const discoveryAddrs: string[] = []
  for (const token of raw.split(/[\s,;]+/)) {
    const entry = token.trim()
    if (!entry) continue
    // 端口按"哪套端口在用"推断: 带端口就用带的, 不带就都补上默认的
    // gRPC/discovery 端口 —— 绝大多数部署用的是默认端口。
    if (entry.includes(':')) {
      grpcAddrs.push(entry)
    } else {
      grpcAddrs.push(`${entry}:50051`)
      discoveryAddrs.push(`${entry}:50052`)
    }
  }
  return { grpcAddrs, discoveryAddrs }
}

// Round 11: 本节点设备 ID + 信任名单 (v3.13.48: 邀请码 JSON 已删除)
const myInvite = ref<ClusterInvite | null>(null)
const allowedDevices = ref<AllowedDevice[]>([])

async function loadInvite(): Promise<void> {
  try {
    myInvite.value = await getClusterInvite()
  } catch {
    myInvite.value = null
  }
}

async function loadAllowed(): Promise<void> {
  try {
    allowedDevices.value = (await listAllowedDevices()).devices ?? []
  } catch {
    allowedDevices.value = []
  }
}

// v3.13.48: 只复制裸设备 ID — "完整邀请码"已删除, 交换物就这一个 ID
async function copyInvite(): Promise<void> {
  if (!myInvite.value) return
  const ok = await copyToClipboard(myInvite.value.device_id)
  if (ok) {
    toast.success(t('toast.savedSuccess'))
  } else {
    toast.error(t('common.copyFailed'))
  }
}

// ---- R12.1: 接受后的连接状态轮询 ----
// v3.19.27: pairingState 增加 rejected / failed 终态 — 对方拒绝或超时后
// 不再对着"连接中"空转; 被拒后提交按钮锁 30 秒 (retryLockUntil), 倒计时
// 结束即可重新点"添加设备"发起新申请 (新 attempt, 对端横幅会再弹)。
const pairingState = ref<'idle' | 'connecting' | 'connected' | 'rejected' | 'failed'>('idle')
let pairingTarget = ''
let pairingPoll = 0
const retryLockUntil = ref(0)
const nowTick = ref(Date.now())
let lockTicker = 0

const retryLockRemaining = computed(() =>
  Math.max(0, Math.ceil((retryLockUntil.value - nowTick.value) / 1000)),
)

function startLockTicker(): void {
  stopLockTicker()
  nowTick.value = Date.now()
  lockTicker = window.setInterval(() => {
    nowTick.value = Date.now()
    if (retryLockRemaining.value <= 0) stopLockTicker()
  }, 1000)
}

function stopLockTicker(): void {
  if (lockTicker) {
    clearInterval(lockTicker)
    lockTicker = 0
  }
}

// v3.19.2: 按设备 ID 精确匹配, 取代原来的"任意非本机节点在线就算成功"。
// 后端 v3.19.2 起在 /api/cluster/nodes 里带 device_id (成员证书指纹) —
// 此前 n.id 是 node.id (config 命名空间), 拿它比对粘贴的指纹永远不等,
// 只能退化成弱判据, 3 节点集群里别的节点上线就会误报"已连接"。
// 保留 node_id 作为兜底: 老版本后端没有 device_id 字段时仍能工作。
function watchPairing(deviceId: string): void {
  pairingTarget = deviceId
  pairingState.value = 'connecting'
  stopPairingPoll()
  const started = Date.now()
  const want = normalizePeerId(deviceId)
  let tick = 0
  pairingPoll = window.setInterval(async () => {
    tick++
    // v3.19.29: 30 秒后降频 1.5s → 3s (前 30 秒是配对握手窗口, 快查快显)
    if (Date.now() - started > 30000 && tick % 2 === 0) return
    try {
      // v3.19.27: 终态优先 — 对方已拒绝/已失败时立即给反馈并停止轮询。
      // at 时间戳防串台: 只认本一次提交 (started) 之后产生的终态。
      const outcome = await getPairingOutcome(deviceId).catch(() => null)
      if (outcome && outcome.state && outcome.state !== 'connected') {
        const outcomeAt = outcome.at ? new Date(outcome.at).getTime() : 0
        if (outcomeAt >= started - 5000) {
          if (outcome.state === 'rejected') {
            pairingState.value = 'rejected'
            retryLockUntil.value = Date.now() + 30000
            startLockTicker()
            stopPairingPoll()
            toast.error(t('cluster.pairing.rejectedByPeer'))
            void qc.invalidateQueries({ queryKey: ['cluster'] })
            return
          }
          if (outcome.state === 'failed') {
            pairingState.value = 'failed'
            stopPairingPoll()
            toast.warning(t('cluster.pairing.requestFailed'))
            return
          }
        }
      }
      const members = await api.get<ClusterMember[]>('/api/cluster/nodes')
      const matched = members.some((n: ClusterMember) =>
        !n.is_local &&
        (normalizePeerId(n.device_id) === want ||
          // 后端未带 device_id 时退化: 用 node_id 比对 (仅当后端把
          // 粘贴值也当 node_id 处理才会命中, 概率低但无害)
          (n.device_id ? false : n.id === want)),
      )
      if (matched) {
        pairingState.value = 'connected'
        stopPairingPoll()
        toast.success(t('cluster.invite.connected'))
        void qc.invalidateQueries({ queryKey: ['cluster'] })
        // v3.19.29: 弹窗里的"已信任名单"是本地 ref (非 vue-query),
        // 对端入群后标签已从"等待配对"变成对端节点名 — 同步刷新。
        void loadAllowed()
      } else if (Date.now() - started > 600000) {
        // 超时放宽到 10 分钟 — 要等对方管理员点"接受" (后端拨号循环同为
        // 10 分钟); 超时后用户可重新提交。
        stopPairingPoll()
        pairingState.value = 'idle'
      }
    } catch {
      /* 网络抖动继续轮询 */
    }
  }, 1500)
}

function stopPairingPoll(): void {
  if (pairingPoll) {
    clearInterval(pairingPoll)
    pairingPoll = 0
  }
}

function normalizePeerId(raw?: string): string {
  return (raw ?? '').replace(/[\s-]+/g, '').toUpperCase()
}

// 定时器不随组件卸载清理 — 用户关掉弹窗后轮询仍在后台每 3 秒打一次
// /api/cluster/nodes, 切页也不停。
onUnmounted(() => {
  stopPairingPoll()
  stopLockTicker()
})

// 节点模式选项: label 走 i18n (computed 跟随语言切换); value 保持后端
// 协议枚举原值 (both/upload_only/download_only), 不可翻译。
const modeOptions = computed<Array<{ value: NodeMode; label: string }>>(() => [
  { value: 'both', label: t('cluster.mode.both') },
  { value: 'upload_only', label: t('cluster.mode.upload_only') },
  { value: 'download_only', label: t('cluster.mode.download_only') },
])

function openEditSelf(): void {
  const meta = metaQuery.data.value
  editForm.name = meta?.name ?? selfNode.value?.name ?? ''
  editForm.group = meta?.group ?? ''
  editForm.mode = (meta?.mode as NodeMode) ?? (selfNode.value?.mode as NodeMode) ?? 'both'
  editForm.data_dir = meta?.data_dir ?? selfNode.value?.data_dir ?? ''
  editingNode.value = selfNode.value
  showEdit.value = true
}

// R13.2: 改名/分组元数据只存本机 — 只有本机节点可编辑, 远端铅笔置灰仅作占位
function isSelfNode(node: ClusterMember): boolean {
  return node.is_local || node.status === 'self'
}

function openAdd(): void {
  addForm.device_id = ''
  pairingState.value = 'idle' // v3.19.27: 清掉上一轮的 rejected/connected 终态文案
  showAdd.value = true
  void loadInvite()
  void loadAllowed()
}

const updateMetaMutation = useMutation({
  mutationFn: (payload: DeviceMeta) => updateDeviceMeta(payload),
  onSuccess: () => {
    toast.success(t('toast.savedSuccess'))
    showEdit.value = false
    void qc.invalidateQueries({ queryKey: ['cluster'] })
  },
})

const setModeMutation = useMutation({
  mutationFn: ({ node_id, mode }: { node_id: string; mode: NodeMode }) =>
    setNodeSettings({ node_id, mode }),
  onSuccess: (resp) => {
    toast.success(resp.message)
    void qc.invalidateQueries({ queryKey: ['cluster'] })
  },
})

const addDeviceMutation = useMutation({
  mutationFn: (payload: {
    device_id: string
    label?: string
    addrs?: string[]
    grpc_addrs?: string[]
  }): Promise<AddDeviceResponse> => addDevice(payload),
  onSuccess: (resp) => {
    toast.success(t('cluster.invite.connecting'))
    if (resp.device_id) watchPairing(resp.device_id)
    void qc.invalidateQueries({ queryKey: ['cluster'] })
    void loadAllowed()
  },
  onError: (err: Error) => {
    // R13: 后端 502 = 对端未开配对窗口 — 错误信息含可操作指引
    toast.error(err.message, 'AFD Console')
  },
})

// v3.13.55: 接受/拒绝 mutation 与 shortFp 已上移至全局 PairingBanner。

const removeDeviceMutation = useMutation({
  mutationFn: (nodeId: string) => removeDevice(nodeId),
  onSuccess: (resp) => {
    toast.success(resp.message)
    showRemoveConfirm.value = null
    void qc.invalidateQueries({ queryKey: ['cluster'] })
  },
})

function submitEdit(): void {
  if (!editingNode.value) return
  updateMetaMutation.mutate({
    name: editForm.name.trim(),
    group: editForm.group.trim(),
    mode: editForm.mode,
    data_dir: editForm.data_dir.trim(),
  })
}

function submitAdd(): void {
  // v3.13.48 (Syncthing 式): 收裸设备 ID — AFD 短 ID = 26 字节哈希
  // base32 = 42 字符 (A-Z2-7)。
  // v3.19.2: 地址输入恢复为可选的"高级设置" — 纯 ID 流程不变, 但跨公网
  // / 静态地址场景必须能显式告诉本机往哪拨。
  const raw = addForm.device_id.trim()
  if (!raw) return
  const deviceId = raw.replace(/[\s-]+/g, '').toUpperCase()
  if (!/^[A-Z2-7]{42}$/.test(deviceId)) {
    toast.error(t('cluster.invite.invalidId'))
    return
  }
  const { grpcAddrs, discoveryAddrs } = parseAddrInput(addForm.addr)
  addDeviceMutation.mutate({
    device_id: deviceId,
    label: '',
    ...(grpcAddrs.length > 0 ? { grpc_addrs: grpcAddrs } : {}),
    ...(discoveryAddrs.length > 0 ? { addrs: discoveryAddrs } : {}),
  })
  addForm.device_id = ''
  addForm.addr = ''
  void loadAllowed()
}

async function removeAllowed(deviceId: string): Promise<void> {
  try {
    await fetch('/api/cluster/allowed-devices?device_id=' + encodeURIComponent(deviceId), {
      method: 'DELETE',
      credentials: 'include',
    })
    await loadAllowed()
    toast.success(t('toast.savedSuccess'))
  } catch {
    toast.error(t('common.deleteFailed'))
  }
}

function confirmRemove(node: ClusterMember): void {
  showRemoveConfirm.value = node
}

function doRemove(): void {
  const node = showRemoveConfirm.value
  if (!node) return
  removeDeviceMutation.mutate(node.id)
}

function changeMode(node: ClusterMember, e: Event): void {
  const mode = (e.target as HTMLSelectElement).value as NodeMode
  setModeMutation.mutate({ node_id: node.id, mode })
}

function formatBytes(n?: number): string {
  if (n == null || n <= 0) return '—'
  const KiB = 1024
  const MiB = 1024 * KiB
  const GiB = 1024 * MiB
  const TiB = 1024 * GiB
  if (n >= TiB) return `${(n / TiB).toFixed(2)} TiB`
  if (n >= GiB) return `${(n / GiB).toFixed(2)} GiB`
  if (n >= MiB) return `${(n / MiB).toFixed(2)} MiB`
  if (n >= KiB) return `${(n / KiB).toFixed(2)} KiB`
  return `${n} B`
}

function nodeStatus(node: ClusterMember): 'online' | 'offline' | 'connecting' {
  if (node.status === 'self') return 'online'
  if (node.online === true) return 'online'
  if (node.online === false) return 'offline'
  const s = String(node.status ?? '')
  if (s.includes('connect') || s.includes('ping')) return 'connecting'
  return s === 'online' ? 'online' : 'offline'
}

function refresh(): void {
  void qc.invalidateQueries({ queryKey: ['cluster'] })
}
</script>

<template>
  <div class="space-y-6">
    <!-- Header card: self device id + add button -->
    <Card :title="t('settings.cluster.title')" :subtitle="t('settings.cluster.subtitle')">
      <template #header>
        <div class="flex items-center gap-2">
          <Button variant="outline" size="sm" :loading="statusQuery.isFetching.value" @click="refresh">
            <template #icon><RefreshCw :size="14" /></template>
            {{ t('common.refresh') }}
          </Button>
          <Button
            variant="primary"
            size="sm"
            :disabled="retryLockRemaining > 0"
            :title="retryLockRemaining > 0 ? t('cluster.pairing.retryIn', { n: retryLockRemaining }) : undefined"
            @click="openAdd"
          >
            <template #icon><Plus :size="14" /></template>
            {{ retryLockRemaining > 0 ? t('cluster.pairing.retryIn', { n: retryLockRemaining }) : t('cluster.addDevice') }}
          </Button>
        </div>
      </template>

      <!-- v3.13.58: 本机 ID 常驻行已移除 — 设备 ID 在「添加设备」弹窗里仍可查看/复制
           (配对交换物), 页面不再占用一整行展示无需常看的机器指纹 -->

      <!-- v3.13.55: 待确认配对横幅已上移至全局 (MainLayout/PairingBanner),
           任何页面都可见 — 旧版只在集群设置页, 操作者经常错过批准窗口 -->

      <!-- Node grid -->
      <div v-if="statusQuery.isLoading.value" class="flex items-center justify-center py-12">
        <Spinner :size="20" :label="t('common.loading')" />
      </div>

      <Empty
        v-else-if="nodes.length === 0"
        :title="t('settings.cluster.noNodes')"
        :description="t('settings.cluster.noNodesHint')"
        :icon="Server"
      >
        <Button variant="primary" size="sm" @click="openAdd">
          <template #icon><Plus :size="14" /></template>
          {{ t('cluster.addDevice') }}
        </Button>
      </Empty>

      <div v-else class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <article
          v-for="node in nodes"
          :key="node.id"
          class="group relative flex flex-col gap-3 rounded-lg border border-border bg-bg-surface p-4 transition-colors hover:border-border-strong"
        >
          <!-- Header: status + name + badges (R13.2) -->
          <div class="flex items-start gap-2">
            <StatusDot :status="nodeStatus(node)" :pulse="nodeStatus(node) === 'online'" />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="truncate text-sm font-semibold text-fg">
                  {{ node.name || node.id.slice(0, 12) }}
                </span>
                <!-- v3.19.49: ping 用统一 Badge 组件渲染 (与「我的设备 ID」同形状), 颜色随延迟语义 -->
                <Badge
                  v-if="node.ping_ms != null"
                  :variant="node.ping_ms < 50 ? 'success' : node.ping_ms < 150 ? 'warning' : 'danger'"
                  :title="t('cluster.pingRtt', { avg: node.ping_avg ?? node.ping_ms })"
                >
                  <Gauge :size="11" class="shrink-0" />{{ node.ping_ms }}ms
                </Badge>
                <!-- v3.19.53: 负载也统一为 Badge — 消除名字下孤零零的灰字行 -->
                <Badge v-if="node.load != null" variant="neutral">
                  {{ t('cluster.card.load') }} {{ node.load }}
                </Badge>
                <Badge v-if="node.is_local || node.status === 'self'" variant="info">
                  {{ t('cluster.invite.myId') }}
                </Badge>
                <Badge v-if="node.group" variant="neutral">{{ node.group }}</Badge>
              </div>
            </div>
          </div>

          <!-- Meta: addr + disk (R13.2 修映射: 后端给 disk 对象) -->
          <div class="space-y-2 text-xs">
            <div class="flex items-center gap-1.5 text-fg-muted">
              <Network :size="12" class="shrink-0" />
              <span class="truncate">{{ node.addr || '—' }}<span v-if="node.grpc_port"> :{{ node.grpc_port }}</span></span>
            </div>
            <div v-if="node.disk?.total">
              <div class="mb-1 flex items-center justify-between gap-2">
                <span class="flex items-center gap-1.5 text-fg-muted">
                  <HardDrive :size="12" class="shrink-0" />
                  {{ formatBytes(node.disk.used) }} / {{ formatBytes(node.disk.total) }}
                </span>
                <span class="font-mono text-fg-subtle">{{ node.disk.percent ?? '—' }}%</span>
              </div>
              <Progress
                :value="node.disk.percent ?? 0"
                :max="100"
                size="sm"
                :variant="(node.disk.percent ?? 0) >= 90 ? 'danger' : (node.disk.percent ?? 0) >= 75 ? 'warning' : 'primary'"
              />
            </div>
            <div v-else class="flex items-center gap-1.5 text-fg-muted">
              <HardDrive :size="12" class="shrink-0" />
              <span class="text-fg-subtle">—</span>
            </div>
          </div>

          <!-- Footer: mode + actions -->
          <div class="flex items-center gap-3 border-t border-border pt-3">
            <label class="flex items-center gap-1.5 text-xs text-fg-muted">
              <span>{{ t('settings.cluster.mode') }}</span>
            </label>
            <select
              class="flex-1 rounded border border-border bg-bg-surface px-2.5 py-1.5 text-xs text-fg focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              :value="node.mode ?? 'both'"
              :disabled="setModeMutation.isPending.value"
              @change="changeMode(node, $event)"
            >
              <option v-for="opt in modeOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>

            <!-- R13.2: 编辑仅本机生效 (openEditSelf 只改本机 meta) — 远端卡片铅笔置灰, 避免找不到入口 -->
            <Tooltip
              :content="isSelfNode(node) ? t('common.edit') : t('cluster.editSelfOnly')"
              placement="top"
            >
              <Button
                variant="ghost"
                size="sm"
                icon-only
                :disabled="!isSelfNode(node)"
                :aria-label="isSelfNode(node) ? t('common.edit') : t('cluster.editSelfOnly')"
                @click="openEditSelf"
              >
                <Pencil :size="14" />
              </Button>
            </Tooltip>
            <Tooltip :content="t('cluster.removeDevice')" placement="top">
              <Button
                variant="ghost"
                size="sm"
                icon-only
                :disabled="node.status === 'self'"
                :aria-label="t('cluster.removeDevice')"
                @click="confirmRemove(node)"
              >
                <Trash2 :size="14" class="text-danger" />
              </Button>
            </Tooltip>
          </div>
        </article>
      </div>
    </Card>

    <!-- Edit node modal -->
    <Modal v-model="showEdit" :title="t('settings.cluster.editNode')" size="md">
      <form class="space-y-3" @submit.prevent="submitEdit">
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('cluster.selfId') }}</label>
          <Input :model-value="editingNode?.id ?? ''" readonly disabled />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('cluster.nodeName') }}</label>
          <Input v-model="editForm.name" :placeholder="t('cluster.nodeName')" />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.cluster.group') }}</label>
          <Input v-model="editForm.group" :list="'cluster-group-list'" :placeholder="t('settings.cluster.groupPlaceholder')" />
          <datalist id="cluster-group-list">
            <option value="default" />
            <option value="edge" />
            <option value="core" />
          </datalist>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.cluster.mode') }}</label>
          <select
            v-model="editForm.mode"
            class="w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option v-for="opt in modeOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.cluster.dataDir') }}</label>
          <Input v-model="editForm.data_dir" placeholder="/data/TrAf" />
        </div>
      </form>
      <template #footer>
        <Button variant="ghost" size="sm" :disabled="updateMetaMutation.isPending.value" @click="showEdit = false">
          {{ t('common.cancel') }}
        </Button>
        <Button variant="primary" size="sm" :loading="updateMetaMutation.isPending.value" @click="submitEdit">
          {{ t('common.save') }}
        </Button>
      </template>
    </Modal>

    <!-- Add device modal (v3.13.48: Syncthing 式 — 交换裸设备 ID, 对方显式接受) -->
    <Modal v-model="showAdd" :title="t('cluster.addDevice')" size="md">
      <div class="space-y-5">
        <!-- 我的设备 ID -->
        <section>
          <p class="mb-2 text-xs font-medium text-fg-muted">{{ t('cluster.invite.myId') }}</p>
          <div class="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2">
            <code class="flex-1 select-all truncate font-mono text-xs text-fg">{{ myInvite?.device_id || '…' }}</code>
            <Button variant="ghost" size="sm" :title="t('cluster.invite.copyId')" @click="copyInvite()">{{ t('cluster.invite.copyId') }}</Button>
          </div>
          <p v-if="myInvite" class="mt-1 text-xs text-fg-subtle">
            {{ t('cluster.invite.inviteHint') }}
          </p>
          <!-- v3.19.2: 展示本机可达地址 + 公网可达性提示。
               云主机的 EIP 不在任何网卡上, 没显式配 public_grpc_addr 时
               跨公网配对必然失败 —— 与其让用户对着转圈的配对界面猜, 不如
               在这一步就说明白。 -->
          <div v-if="myInvite && myInvite.grpc_addrs?.length" class="mt-2 rounded-lg border border-border bg-muted px-3 py-2">
            <p class="text-xs font-medium text-fg-muted">{{ t('cluster.invite.myAddrs') }}</p>
            <ul class="mt-1 space-y-0.5">
              <li v-for="a in myInvite.grpc_addrs" :key="a">
                <code class="select-all font-mono text-xs text-fg">{{ a }}</code>
              </li>
            </ul>
          </div>
          <p v-if="myInvite && myInvite.has_public_addr === false" class="mt-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-fg-muted">
            {{ t('cluster.invite.noPublicAddr') }}
          </p>
        </section>

        <!-- 添加对方的设备 -->
        <section>
          <p class="mb-2 text-xs font-medium text-fg-muted">{{ t('cluster.invite.acceptPeer') }}</p>
          <form class="flex gap-2" @submit.prevent="submitAdd">
            <Input v-model="addForm.device_id" class="flex-1 font-mono" :placeholder="t('cluster.invite.peerPlaceholder')" />
            <Button
              variant="primary"
              size="sm"
              :loading="addDeviceMutation.isPending.value"
              :disabled="!addForm.device_id.trim() || retryLockRemaining > 0"
              :title="retryLockRemaining > 0 ? t('cluster.pairing.retryIn', { n: retryLockRemaining }) : undefined"
              @click="submitAdd"
            >
              {{ retryLockRemaining > 0 ? t('cluster.pairing.retryIn', { n: retryLockRemaining }) : t('cluster.invite.accept') }}
            </Button>
          </form>
          <!-- v3.19.2: 手动地址输入恢复 (折叠)。同网段可以不填, 靠 gossip;
               跨公网/静态地址必须填 —— 设备 ID 只是证书哈希, 里面没有地址。 -->
          <div class="mt-2">
            <button
              type="button"
              class="text-xs text-fg-subtle underline-offset-2 hover:underline"
              @click="showAdvanced = !showAdvanced"
            >
              {{ showAdvanced ? t('cluster.invite.hideAdvanced') : t('cluster.invite.showAdvanced') }}
            </button>
            <div v-if="showAdvanced" class="mt-2 space-y-1">
              <Input
                v-model="addForm.addr"
                class="font-mono"
                :placeholder="t('cluster.invite.addrPlaceholder')"
              />
              <p class="text-xs text-fg-subtle">{{ t('cluster.invite.addrHint') }}</p>
            </div>
          </div>
          <!-- R12.1: 接受后的连接状态反馈 -->
          <p v-if="pairingState === 'connecting'" class="mt-2 flex items-center gap-2 text-xs text-fg-muted">
            <Spinner :size="14" /> {{ t('cluster.invite.connecting') }}
          </p>
          <p v-else-if="pairingState === 'rejected'" class="mt-2 text-xs text-danger">
            {{ t('cluster.pairing.rejectedByPeer') }}
          </p>
          <p v-else-if="pairingState === 'failed'" class="mt-2 text-xs text-warning">
            {{ t('cluster.pairing.requestFailed') }}
          </p>
          <p v-else-if="pairingState === 'connected'" class="mt-2 text-xs text-success">
            {{ t('cluster.invite.connected') }}
          </p>
        </section>

        <!-- 已信任名单 -->
        <section v-if="allowedDevices.length">
          <p class="mb-2 text-xs font-medium text-fg-muted">{{ t('cluster.invite.trustedList') }}</p>
          <ul class="space-y-1">
            <li v-for="d in allowedDevices" :key="d.device_id" class="flex items-center justify-between rounded-lg border border-border px-3 py-1.5 text-xs">
              <code class="truncate font-mono text-fg">{{ d.label || d.device_id }}</code>
              <button class="shrink-0 text-fg-subtle hover:text-danger" @click="removeAllowed(d.device_id)">✕</button>
            </li>
          </ul>
        </section>
      </div>
      <template #footer>
        <Button variant="ghost" size="sm" @click="showAdd = false">{{ t('common.close') }}</Button>
      </template>
    </Modal>

    <!-- Remove device confirm -->
    <Modal v-model="showRemoveConfirm" :title="t('settings.cluster.removeConfirmTitle')" size="sm">
      <div class="flex items-start gap-3">
        <div class="rounded-md bg-danger/15 p-2 text-danger shrink-0">
          <AlertTriangle :size="18" />
        </div>
        <div class="text-sm text-fg-muted">
          <p>{{ t('settings.cluster.removeConfirm', { name: showRemoveConfirm?.name || showRemoveConfirm?.id }) }}</p>
          <p class="mt-2 text-xs text-fg-subtle">{{ t('settings.cluster.removeHint') }}</p>
        </div>
      </div>
      <template #footer>
        <Button variant="ghost" size="sm" @click="showRemoveConfirm = null">
          {{ t('common.cancel') }}
        </Button>
        <Button variant="danger" size="sm" :loading="removeDeviceMutation.isPending.value" @click="doRemove">
          {{ t('cluster.removeDevice') }}
        </Button>
      </template>
    </Modal>
  </div>
</template>
